// ویرایشگر نسخه پیش‌نویس پلن (SCR-CONSOLE-004؛ US-PLT-020): قیمت‌ها، Featureها با چک‌باکس گروه‌بندی‌شده و همه Limit Keyها
// (AC-PLT-020.2: کامل بودن کلیدها در Client و سرور — PHX-PLT-0202). API فقط «نسخه تازه» دارد، پس ویرایشگر از آخرین نسخه پر می‌شود.
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type PlanVersionRequest, type PlanView } from '@phoenix/api-client';
import { useIdempotencyKey, usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { CheckboxRHF, FormDialog, MoneyFieldRHF, TextFieldRHF, zMoneyMinor } from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useController, useForm, useWatch, type Control } from 'react-hook-form';
import { z } from 'zod';
import { consoleKeys, latestVersion, toNumber } from '../../api';
import { FEATURE_GROUPS, LIMIT_KEYS, limitLabelKey, moduleLabelKey } from '../../catalog';

const schema = z.object({
  basePriceMinor: zMoneyMinor(),
  perProviderPriceMinor: zMoneyMinor(),
  features: z.array(z.string()),
  limits: z
    .array(z.object({ key: z.string(), value: z.string(), unlimited: z.boolean() }))
    .superRefine((limits, ctx) => {
      limits.forEach((limit, index) => {
        if (!limit.unlimited && !/^\d{1,9}$/.test(limit.value.trim())) {
          ctx.addIssue({
            code: 'custom',
            path: [index, 'value'],
            message: 'console:validation.limit_required',
          });
        }
      });
    }),
});

type FormInput = z.input<typeof schema>;
type FormOutput = z.output<typeof schema>;

function FeatureChecklist({ control }: { control: Control<FormInput, unknown, FormOutput> }) {
  const { t } = useTranslation('console');
  const { field } = useController({ control, name: 'features' });
  const selected = new Set(field.value);
  const toggle = (feature: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) {
      next.add(feature);
    } else {
      next.delete(feature);
    }
    field.onChange([...next].sort());
  };
  return (
    <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
      <FormLabel component="legend">{t('console:plans.editor.features')}</FormLabel>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1,
          mt: 1,
        }}
        data-testid="feature-checklist"
      >
        {FEATURE_GROUPS.map((group) => (
          <Box key={group.module}>
            <Typography variant="caption" color="text.secondary">
              {t(moduleLabelKey(group.module))}
            </Typography>
            <FormGroup>
              {group.features.map((feature) => (
                <FormControlLabel
                  key={feature}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.has(feature)}
                      onChange={(event) => toggle(feature, event.target.checked)}
                      slotProps={{ input: { 'aria-label': feature } }}
                    />
                  }
                  label={<bdi dir="ltr">{feature}</bdi>}
                />
              ))}
            </FormGroup>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function LimitFields({ control }: { control: Control<FormInput, unknown, FormOutput> }) {
  const { t } = useTranslation('console');
  const limits = useWatch({ control, name: 'limits' });
  return (
    <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
      <FormLabel component="legend">{t('console:plans.editor.limits')}</FormLabel>
      <FormHelperText>{t('console:plans.editor.limits_help')}</FormHelperText>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {LIMIT_KEYS.map((key, index) => (
          <Stack key={key} direction="row" spacing={2} alignItems="flex-start" data-limit-key={key}>
            <TextFieldRHF
              control={control}
              name={`limits.${index}.value`}
              labelKey={limitLabelKey(key)}
              disabled={limits?.[index]?.unlimited === true}
              slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', 'data-testid': `limit-${key}` } }}
              sx={{ flex: 1 }}
            />
            <CheckboxRHF
              control={control}
              name={`limits.${index}.unlimited`}
              labelKey="console:plans.editor.unlimited"
            />
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export function PlanVersionDialog({
  plan,
  open,
  onClose,
}: {
  plan: PlanView;
  open: boolean;
  onClose: () => void;
}) {
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();

  const defaults = useMemo<FormInput>(() => {
    const source = latestVersion(plan);
    return {
      basePriceMinor: toNumber(source?.basePriceMinor) ?? 0,
      perProviderPriceMinor: toNumber(source?.perProviderPriceMinor) ?? 0,
      features: [...(source?.features ?? [])].sort(),
      limits: LIMIT_KEYS.map((key) => {
        const present = source ? Object.prototype.hasOwnProperty.call(source.limits, key) : false;
        const value = present ? toNumber(source?.limits[key]) : null;
        return { key, value: value === null ? '' : String(value), unlimited: present && value === null };
      }),
    };
  }, [plan]);

  const { control, handleSubmit, formState, reset } = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(schema),
    values: defaults,
  });

  const create = useMutation({
    mutationFn: (body: PlanVersionRequest) =>
      unwrap(
        api.POST('/api/v1/platform/console/plans/{id}/versions', {
          params: { path: { id: plan.id } },
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consoleKeys.plans });
      onClose();
    },
    onError: (error) => setSubmitError(error),
  });

  const submit = handleSubmit((values) =>
    create.mutate({
      basePriceMinor: values.basePriceMinor,
      perProviderPriceMinor: values.perProviderPriceMinor,
      currency: 'IRR',
      features: values.features,
      limits: Object.fromEntries(
        values.limits.map((limit) => [limit.key, limit.unlimited ? null : Number(limit.value.trim())]),
      ),
    }),
  );

  return (
    <FormDialog
      open={open}
      screenId="SCR-CONSOLE-004-VERSION"
      titleKey="console:plans.editor.title"
      onSubmit={() => void submit()}
      onClose={() => {
        reset(defaults);
        setSubmitError(undefined);
        onClose();
      }}
      isDirty={formState.isDirty}
      isSubmitting={create.isPending}
      submitError={submitError}
      submitKey="console:plans.editor.submit"
      maxWidth="md"
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <MoneyFieldRHF
          control={control}
          name="basePriceMinor"
          labelKey="console:plans.editor.base_price"
          unit="Rial"
          required
          fullWidth
        />
        <MoneyFieldRHF
          control={control}
          name="perProviderPriceMinor"
          labelKey="console:plans.editor.per_provider_price"
          unit="Rial"
          required
          fullWidth
        />
      </Stack>
      <FeatureChecklist control={control} />
      <LimitFields control={control} />
    </FormDialog>
  );
}
